import {
  Button,
  Flex,
  Heading,
  HStack,
  Link,
  VStack,
  Text,
  Center,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { FunctionComponent, useEffect, useState } from 'react'
import Card from '../../common/components/Card'
import { Logo } from '../../components/shared-components/Logo'
import { enableIntercomService } from '../../services/intercom/intercomService'
import GoogleAnalyticsTagManager from '../../utils/google-analytics/GoogleAnalyticsTagManager'
import { MAINTENANCE_INFO, MONTH_LIST } from '../../constants'
import { ssoStorage } from '../../features/sso'

const LoginPage: FunctionComponent<unknown> = () => {
  const isExpired = Boolean(localStorage.getItem('_isExpired'))
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState('')
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [ssoErrorDescription, setSsoErrorDescription] = useState<string | null>(null)

  const getErrorMessage = (error: string | null, errorDescription: string | null) => {
    if (!error && !errorDescription) return null

    if (
      errorDescription?.toLowerCase().includes('email') &&
      errorDescription?.toLowerCase().includes('verified')
    ) {
      return "We couldn't complete your sign-in. Your email address needs to be verified with your authentication provider. Please check your email for a verification link and try signing in again."
    }

    if (
      errorDescription?.toLowerCase().includes('account') &&
      errorDescription?.toLowerCase().includes('disabled')
    ) {
      return "We couldn't complete your sign-in. Your account appears to be disabled. Please contact your administrator for assistance."
    }

    if (
      errorDescription?.toLowerCase().includes('invalid') &&
      errorDescription?.toLowerCase().includes('credentials')
    ) {
      return "We couldn't complete your sign-in. Please check your credentials and try again."
    }

    // Default message for other errors
    return "We couldn't complete your sign-in. Please try again or contact support if the problem persists."
  }

  useEffect(() => {
    enableIntercomService()

    // Check for SSO errors in localStorage
    const error = localStorage.getItem('sso_error')
    const errorDescription = localStorage.getItem('sso_error_description')

    if (error || errorDescription) {
      setSsoError(error)
      setSsoErrorDescription(errorDescription)
      localStorage.removeItem('sso_error')
      localStorage.removeItem('sso_error_description')
    }

    const currentTime = Math.floor(Date.now() / 1000)
    const formatMaintenanceTime = () => {
      const startDate = new Date(MAINTENANCE_INFO.startTime * 1000)
      const endDate = new Date(MAINTENANCE_INFO.endTime * 1000)

      const startDay = startDate.getDate()
      const startMonth = MONTH_LIST[startDate.getMonth()]
      const startYear = startDate.getFullYear()

      const startHours = startDate.getHours()
      const startMinutes = startDate.getMinutes()
      const startAmPm = startHours >= 12 ? 'PM' : 'AM'
      const formattedStartHours = startHours % 12 || 12
      const formattedStartMinutes = startMinutes.toString().padStart(2, '0')

      const endHours = endDate.getHours()
      const endMinutes = endDate.getMinutes()
      const endAmPm = endHours >= 12 ? 'PM' : 'AM'
      const formattedEndHours = endHours % 12 || 12
      const formattedEndMinutes = endMinutes.toString().padStart(2, '0')

      return `System is currently undergoing scheduled maintenance on ${startDay} ${startMonth} ${startYear} from ${formattedStartHours}:${formattedStartMinutes} ${startAmPm} to ${formattedEndHours}:${formattedEndMinutes} ${endAmPm} AEST. Services will be unavailable during this time. We apologise for any inconvenience.`
    }

    if (currentTime >= MAINTENANCE_INFO.startTime && currentTime <= MAINTENANCE_INFO.endTime) {
      setIsMaintenanceMode(true)
      setMaintenanceMessage(formatMaintenanceTime())
    }
  }, [])

  const authUri = process.env.REACT_APP_COGNITO_AUTH_URI
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID
  const redirectUri = `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${clientId}`

  const handleSignIn = () => {
    GoogleAnalyticsTagManager.sendLoginEvent()
    // Clear any existing SSO errors
    setSsoError(null)
    setSsoErrorDescription(null)
    const redirectUrl = ssoStorage.getRedirectUrl()
    localStorage.clear()
    if (redirectUrl) {
      ssoStorage.setRedirectUrl(redirectUrl)
    }
  }

  const handleSignUp = () => {
    GoogleAnalyticsTagManager.sendSignUpEvent()
    setSsoError(null)
    setSsoErrorDescription(null)
  }

  return (
    <Flex height="100%" flexDirection="column" p={10}>
      <HStack mt={20}>
        <VStack spacing="16" width="100%">
          <Card variant="light" padding="8" width="90%" maxWidth="500px">
            <VStack spacing="20px" alignItems="stretch">
              <Center>
                <Logo maxWidth={150} />
              </Center>
              <Heading as="h2" alignSelf="center" size="lg">
                Login
              </Heading>
              {isExpired && !isMaintenanceMode && (
                <Text alignSelf="center">You have been logged out due to inactivity</Text>
              )}
              {getErrorMessage(ssoError, ssoErrorDescription) && !isMaintenanceMode && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text>{getErrorMessage(ssoError, ssoErrorDescription)}</Text>
                </Alert>
              )}
              {isMaintenanceMode ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text>{maintenanceMessage}</Text>
                </Alert>
              ) : (
                <>
                  <Link
                    href={`${authUri}/login?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`}
                    onClick={handleSignIn}
                  >
                    <Button colorScheme="blue" alignSelf="center" style={{ width: '100%' }}>
                      Sign In
                    </Button>
                  </Link>
                  <Link
                    href={`${authUri}/signup?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`}
                    onClick={handleSignUp}
                  >
                    <Button colorScheme="blue" alignSelf="center" style={{ width: '100%' }}>
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </VStack>
          </Card>
        </VStack>
      </HStack>
    </Flex>
  )
}

export default LoginPage
